import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { socket } from '../../socket';
import './ConnectionStatus.css';

const ConnectionStatus: React.FC = () => {
  const [status, setStatus] = useState<'connected' | 'reconnecting' | 'disconnected'>('connected');
  const [attempt, setAttempt] = useState(0);
  const [showRecovered, setShowRecovered] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const wasDisconnectedRef = useRef(false);

  useEffect(() => {
    const onConnect = () => {
      if (wasDisconnectedRef.current) {
        setShowRecovered(true);
        setTimeout(() => setShowRecovered(false), 3000);
      }
      wasDisconnectedRef.current = false;
      setStatus('connected');
      setAttempt(0);
    };

    const onDisconnect = () => {
      wasDisconnectedRef.current = true;
      setStatus('reconnecting');
    };

    const onReconnectAttempt = (n: number) => {
      setAttempt(n);
      setStatus('reconnecting');
    };

    const onReconnectFailed = () => {
      setStatus('disconnected');
    };

    const onReconnectError = () => {
      setStatus('reconnecting');
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.io.on('reconnect_attempt', onReconnectAttempt);
    socket.io.on('reconnect_failed', onReconnectFailed);
    socket.io.on('reconnect_error', onReconnectError);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.io.off('reconnect_attempt', onReconnectAttempt);
      socket.io.off('reconnect_failed', onReconnectFailed);
      socket.io.off('reconnect_error', onReconnectError);
    };
  }, []);

  useEffect(() => {
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const handleRetry = () => {
    setStatus('reconnecting');
    setAttempt(0);
    socket.connect();
  };

  return (
    <>
      <AnimatePresence>
        {status === 'reconnecting' && (
          <motion.div
            className="conn-banner reconnecting"
            initial={{ y: -60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -60, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          >
            <span className="conn-spinner" />
            <span className="conn-text">
              Reconnecting to server{attempt > 0 ? ` (attempt ${attempt}/10)` : ''}...
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {status === 'disconnected' && (
          <motion.div
            className="conn-banner disconnected"
            initial={{ y: -60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -60, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          >
            <span className="conn-icon">⚠</span>
            <span className="conn-text">Server not responding</span>
            <button className="conn-retry" onClick={handleRetry}>Retry</button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showRecovered && (
          <motion.div
            className="conn-banner recovered"
            initial={{ y: -60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -60, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          >
            <span className="conn-icon">✓</span>
            <span className="conn-text">Connection restored</span>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOffline && (
          <motion.div
            className="conn-banner offline"
            role="status"
            aria-live="polite"
            initial={{ y: -60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -60, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          >
            <span className="conn-icon">📵</span>
            <span className="conn-text">No internet — live play requires a server connection</span>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ConnectionStatus;
